import { slugResolver } from '../../art/raster';
import { flatCollectible } from '../../art/flat-collectible';
import type { ScenePlacement } from '../../art/content-plugin';

/**
 * Per-scene PLACEMENT (ADR-0110 §7) — where the hero stands and the pet sits ON each backdrop, since every
 * scene's floor sits at a different spot. Coordinates are the character's feet (bottom-centre) as a percentage
 * (0–100) of the SQUARE scene; the hero is anchored by its feet, the pet beside it. Tuned to each painting's
 * ground line (the meadow's grass, the night hill, the ballroom carpet, the castle's clouds…). A slug omitted
 * here falls back to the centred default in HeroStage.
 */
const PLACEMENT: Record<string, ScenePlacement> = {
  meadow: { hero: { x: 50, y: 88 }, pet: { x: 70, y: 92 } },
  forest: { hero: { x: 49, y: 90 }, pet: { x: 70, y: 94 } },
  night: { hero: { x: 48, y: 89 }, pet: { x: 66, y: 92 } },
  castle: { hero: { x: 50, y: 90 }, pet: { x: 70, y: 93 } },
  ballroom: { hero: { x: 50, y: 91 }, pet: { x: 68, y: 94 } },
  orchard: { hero: { x: 48, y: 90 }, pet: { x: 68, y: 93 } },
  beach: { hero: { x: 50, y: 90 }, pet: { x: 70, y: 93 } },
  snow: { hero: { x: 49, y: 91 }, pet: { x: 68, y: 94 } },
  waterfall: { hero: { x: 50, y: 92 }, pet: { x: 70, y: 95 } },
  oasis: { hero: { x: 50, y: 91 }, pet: { x: 70, y: 94 } },
  sakura: { hero: { x: 50, y: 92 }, pet: { x: 72, y: 95 } },
  cottage: { hero: { x: 50, y: 91 }, pet: { x: 70, y: 94 } },
  carnival: { hero: { x: 50, y: 90 }, pet: { x: 70, y: 93 } },
  tower: { hero: { x: 50, y: 91 }, pet: { x: 70, y: 94 } },
  rainbow: { hero: { x: 50, y: 88 }, pet: { x: 70, y: 91 } },
  cave: { hero: { x: 50, y: 90 }, pet: { x: 70, y: 93 } },
  skyland: { hero: { x: 49, y: 92 }, pet: { x: 70, y: 95 } },
  grotto: { hero: { x: 50, y: 91 }, pet: { x: 70, y: 94 } },
  space: { hero: { x: 50, y: 90 }, pet: { x: 70, y: 93 } },
  skyline: { hero: { x: 50, y: 91 }, pet: { x: 70, y: 94 } },
  station: { hero: { x: 48, y: 90 }, pet: { x: 70, y: 93 } },
  reef: { hero: { x: 50, y: 91 }, pet: { x: 70, y: 94 } },
  harbor: { hero: { x: 50, y: 92 }, pet: { x: 72, y: 95 } },
  bedroom: { hero: { x: 50, y: 92 }, pet: { x: 72, y: 95 } },
  treehouse: { hero: { x: 50, y: 90 }, pet: { x: 70, y: 93 } },
};

/**
 * The BACKGROUND content-plugin (ADR-0110 §7 · ADR-0114) — a flat collectible like a pet, but it ALSO has a
 * staged backdrop: its square tile fills 1:1 (`thumb` fit = `slice`) for the shop/collection, and `withStage`
 * exposes the full-bleed 160×210 backdrop + legibility scrim HeroStage draws behind ANY hero. The
 * `import.meta.glob` literal stays here (Vite resolves it at build); each art file's stem IS its background
 * slug. WEB-ONLY (the art subpath), never imported by the API/Node.
 */
const backgroundAssetFor = slugResolver(
  import.meta.glob('./art/*.webp', { eager: true, query: '?url', import: 'default' }),
);

export const backgroundPlugin = flatCollectible({
  kind: 'background',
  assetFor: backgroundAssetFor,
  tileFit: 'thumb',
  withStage: true,
  placementFor: (slug) => PLACEMENT[slug],
});
